import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { getAllProducts } from "../services/Public/PublicPagesApi";

const ProductsContext = createContext(null);

export const ProductsProvider = ({ children }) => {
  const [rawGroups, setRawGroups] = useState([]); // as returned by API: array of { category, products }
  const [flatProducts, setFlatProducts] = useState([]); // flattened products list
  const [grouped, setGrouped] = useState({}); // grouped by category id/name
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const buildStructures = (resArray) => {
    const groupedObj = {};
    const flat = [];

    (resArray || []).forEach((entry) => {
      const cat = entry.category || {};
      const key = cat.id ?? cat.name_en ?? cat.name_ar ?? "uncategorized";
      const products = Array.isArray(entry.products) ? entry.products : [];

      // ensure each product has categroy property for compatibility
      const normalized = products.map((p) => ({
        ...p,
        categroy: p.categroy || cat,
      }));

      groupedObj[key] = normalized;
      normalized.forEach((p) => flat.push(p));
    });

    return { groupedObj, flat };
  };

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAllProducts();
      // support different response shapes
      const list = res?.products || res?.data || res?.data?.products || [];
      setRawGroups(list);

      const { groupedObj, flat } = buildStructures(list);
      setGrouped(groupedObj);
      setFlatProducts(flat);
      setLoading(false);
      return { success: true, raw: list };
    } catch (err) {
      setError(err?.message || err?.response?.data?.message || "Failed to load products");
      setLoading(false);
      return { success: false, error: err };
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const value = {
    rawGroups,
    flatProducts,
    grouped,
    loading,
    error,
    refresh: fetchProducts,
  };

  return <ProductsContext.Provider value={value}>{children}</ProductsContext.Provider>;
};

export const useProducts = () => {
  const ctx = useContext(ProductsContext);
  if (!ctx) {
    throw new Error("useProducts must be used within ProductsProvider");
  }
  return ctx;
};

export default ProductsContext;
